/**
 * The Featured placements (PD §3; P/Member_Hub.html:1268-1645) as data: the types the surface reads off
 * `researchPortalConfig.adPlacements` (the `research.ads.placements` System Setting, seeded by V076), and the pure rules that
 * decide which creative mounts in which slot.
 *
 * Screens. A host names the screen it renders as one of `home | grants | people | jobs | career-map | saved | profile | search |
 * settings`, or a detail as `detail:<module>` (`DetailAdScreen`). A placement matches when its `screens` list holds the screen's
 * base ('detail' for any detail). The prototype picks the two display ads by page title — the house (AGU) creative when the title
 * matches `/grant|career/`, the paid one otherwise (P/Member_Hub.html:1415-1418, 1453-1455) — so on a screen both match (the seed
 * lists 'detail' on lb-paid AND lb-house), the grants context takes the house creative and every other context the paid one.
 */

/** Every slot kind a host can mount; the server's RESEARCH_AD_KINDS (apps/MJAPI/src/resolvers/research/adPlacements.ts) mirrors it. */
export const AD_KINDS = ['leaderboard', 'sidebar', 'feature', 'strip', 'rail-spot', 'in-list-job', 'circles', 'no-results-line', 'detail-circles'] as const;

export type AGUAdKind = (typeof AD_KINDS)[number];

export function IsAGUAdKind(value: string): value is AGUAdKind {
    return (AD_KINDS as readonly string[]).includes(value);
}

/** One creative, field for field the GraphQL `ResearchAdPlacement` with `kind` narrowed. */
export interface AGUAdPlacement {
    id: string;
    kind: AGUAdKind;
    screens: readonly string[];
    /** True for an AGU (house) creative: tagged "From AGU"; false for a paid one: "Featured" (P/Member_Hub.html:1277-1278). */
    house: boolean;
    logoText: string | null;
    logoBg: string | null;
    /** True when the logo is the AGU mark rather than initials on `logoBg`. */
    logoIsAgu: boolean;
    head: string;
    /** Free text; `circles` and `detail-circles` pack several rows with `|` — see `SplitPackedSub`. */
    sub: string;
    cta: string;
    /** Null renders the CTA as a non-link (every seeded creative this round). */
    href: string | null;
    /** Entries sharing a value rotate in one slot (`FeatureRotation`). */
    rotate: string | null;
    /** `detail-circles` only: the person display name the creative belongs to. */
    personMatch: string | null;
    /** True for prototype copy. */
    mock: boolean;
}

/** The display kinds: one creative per slot, house or paid by context (P/Member_Hub.html:1415-1418, 1453-1455). */
const SINGLE_KINDS: readonly AGUAdKind[] = ['leaderboard', 'sidebar'];

/** The screen name of a module's detail view: 'detail:grants', 'detail:people', 'detail:jobs'. */
export function DetailAdScreen(moduleKey: string): string {
    return `detail:${moduleKey}`;
}

/** The creatives of `kind` that mount on `screen`, minus the session's dismissed ids. A display kind yields at most one. */
export function SelectPlacements(placements: readonly AGUAdPlacement[], screen: string, kind: AGUAdKind, dismissed: ReadonlySet<string>): AGUAdPlacement[] {
    const [base, context = base] = screen.split(':');
    const matches = placements.filter((p) => p.kind === kind && p.screens.includes(base));
    if (!SINGLE_KINDS.includes(kind)) return matches.filter((p) => !dismissed.has(p.id));
    const wantHouse = context === 'grants';
    const pick = matches.find((p) => p.house === wantHouse) ?? matches[0];
    // A dismissed display creative leaves its slot empty for the session — the prototype returns rather than swapping in the other (:1426, :1463).
    return pick && !dismissed.has(pick.id) ? [pick] : [];
}

/** The id at `index` (wrapping, negative-safe) among the rotating creatives; null when there are none (P/Member_Hub.html:1515-1517). */
export function FeatureRotation(ids: readonly string[], index: number): AGUAdPlacement['id'] | null {
    if (ids.length === 0) return null;
    return ids[((index % ids.length) + ids.length) % ids.length];
}

/**
 * The rows packed into a `circles` / `detail-circles` `sub` (V076 seed): a lede, then one row per Circle, separated by `|`. The
 * setting holds one string per creative, so the prototype's lede-plus-cards (P/Member_Hub.html:1586-1610) and note-plus-rows
 * (:1629-1644) travel as `"<lede> | <row> | <row>"`.
 */
export function SplitPackedSub(sub: string): string[] {
    return sub.split('|').map((part) => part.trim()).filter((part) => part.length > 0);
}

export interface AGUAdCircleRow {
    Name: string;
    Count: string;
    Body: string;
}

/** One packed Circle row, `"<name>: <count>. <body>"` (body optional) → its parts (P/Member_Hub.html:1587-1590). */
export function SplitCircleRow(row: string): AGUAdCircleRow {
    const colon = row.indexOf(':');
    const name = colon < 0 ? row : row.slice(0, colon);
    const rest = colon < 0 ? '' : row.slice(colon + 1).trim();
    const stop = rest.indexOf('. ');
    return {
        Name: name.trim(),
        Count: (stop < 0 ? rest : rest.slice(0, stop)).trim(),
        Body: stop < 0 ? '' : rest.slice(stop + 2).trim(),
    };
}

export interface AGUAdJobParts {
    Match: string;
    Org: string;
    Salary: string;
    SalaryNote: string;
    Sponsor: string;
}

/**
 * The `in-list-job` `sub`, `"<n>% match · <org> · <place> · <age> · <salary> Est. salary · Sponsored by <sponsor>"` (V076 seed) →
 * the featured job card's pill, org line, salary and band sub (P/Member_Hub.html:1549-1561).
 */
export function SplitInListJobSub(sub: string): AGUAdJobParts {
    const parts = sub.split(' · ').map((p) => p.trim()).filter((p) => p.length > 0);
    const take = (test: (p: string) => boolean): string => {
        const i = parts.findIndex(test);
        return i < 0 ? '' : parts.splice(i, 1)[0];
    };
    const match = take((p) => /% match$/i.test(p));
    const sponsor = take((p) => /^Sponsored by /i.test(p));
    const salaryPart = take((p) => /Est\. salary$/i.test(p));
    const salary = salaryPart.replace(/\s*Est\. salary$/i, '');
    return { Match: match, Org: parts.join(' · '), Salary: salary, SalaryNote: salaryPart.length > 0 ? 'Est. salary' : '', Sponsor: sponsor };
}

export interface AGUAdRailParts {
    Line: string;
    Body: string;
}

/** The `rail-spot` `sub`, `"<sector · place>. <body>"` (V076 seed) → the spotlight's sub line and paragraph (P/Member_Hub.html:1538-1539). */
export function SplitRailSpotSub(sub: string): AGUAdRailParts {
    const stop = sub.indexOf('. ');
    if (stop < 0) return { Line: sub.trim(), Body: '' };
    return { Line: sub.slice(0, stop).trim(), Body: sub.slice(stop + 2).trim() };
}
